import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Check, X, Trash2, MapPin, Calendar, Clock, Loader2, Users, Mail, Lock, Newspaper, Plus, Edit, Upload, FileText, Image, Download, FileSpreadsheet, AlertCircle, CheckCircle, Search, Eye, Database } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";

// Lazy load the rich text editor
const RichTextEditor = lazy(() => import("@/components/RichTextEditor"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADMIN_PASSWORD = "Sagredasogno123.,"; // In production, use env variable

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  
  const [pendingSagre, setPendingSagre] = useState([]);
  const [allSagre, setAllSagre] = useState([]);
  const [allSagreTotal, setAllSagreTotal] = useState(0);
  const [newsletterSubs, setNewsletterSubs] = useState([]);
  const [news, setNews] = useState([]);
  const [regioni, setRegioni] = useState([]);
  const [province, setProvince] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  
  // All sagre filters
  const [allSagreFilter, setAllSagreFilter] = useState({ status: "", regione: "", search: "" });
  const [allSagreLoading, setAllSagreLoading] = useState(false);
  
  // Edit sagra state
  const [editingSagra, setEditingSagra] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [locandinaFile, setLocandinaFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // CSV Import state
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvAutoApprove, setCsvAutoApprove] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const csvInputRef = useRef(null);
  
  // News form
  const [newsForm, setNewsForm] = useState({
    titolo: "",
    contenuto: "",
    categoria: "",
    regione: "",
    immagine_url: ""
  });
  const [newsLoading, setNewsLoading] = useState(false);

  useEffect(() => {
    // Check if already authenticated in session
    const auth = sessionStorage.getItem("admin_auth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      fetchAllSagre();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem("admin_auth", "true");
      setPasswordError("");
    } else {
      setPasswordError("Password non corretta");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("admin_auth");
  };

  const fetchData = async () => {
    try {
      const [pendingRes, newsletterRes, newsRes, regioniRes] = await Promise.all([
        axios.get(`${API}/admin/sagre-pending`),
        axios.get(`${API}/newsletter/subscribers`),
        axios.get(`${API}/news`),
        axios.get(`${API}/regioni`)
      ]);
      setPendingSagre(pendingRes.data);
      setNewsletterSubs(newsletterRes.data);
      setNews(newsRes.data);
      setRegioni(regioniRes.data.regioni);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSagre = async (filters = allSagreFilter) => {
    setAllSagreLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.regione) params.append('regione', filters.regione);
      if (filters.search) params.append('search', filters.search);
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/admin/sagre-all?${params.toString()}`);
      setAllSagre(response.data.sagre);
      setAllSagreTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching all sagre:", error);
    } finally {
      setAllSagreLoading(false);
    }
  };

  const handleAllSagreFilterChange = (key, value) => {
    const newFilters = { ...allSagreFilter, [key]: value };
    setAllSagreFilter(newFilters);
    fetchAllSagre(newFilters);
  };

  const handleDeleteSagraFromAll = async (sagraId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa sagra?")) return;
    
    setActionLoading(prev => ({ ...prev, [sagraId]: 'delete' }));
    try {
      await axios.delete(`${API}/admin/sagre/${sagraId}`);
      setAllSagre(prev => prev.filter(s => s.id !== sagraId));
      setAllSagreTotal(prev => prev - 1);
      toast.success("Sagra eliminata");
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setActionLoading(prev => ({ ...prev, [sagraId]: null }));
    }
  };

  const handleChangeSagraStatus = async (sagraId, newStatus) => {
    setActionLoading(prev => ({ ...prev, [sagraId]: 'status' }));
    try {
      if (newStatus === 'approved') {
        await axios.put(`${API}/admin/sagre/${sagraId}/approve`);
      } else if (newStatus === 'rejected') {
        await axios.put(`${API}/admin/sagre/${sagraId}/reject`);
      }
      setAllSagre(prev => prev.map(s => s.id === sagraId ? { ...s, status: newStatus } : s));
      toast.success(`Stato aggiornato: ${newStatus === 'approved' ? 'Approvata' : 'Rifiutata'}`);
    } catch (error) {
      toast.error("Errore durante l'aggiornamento");
    } finally {
      setActionLoading(prev => ({ ...prev, [sagraId]: null }));
    }
  };

  const handleApprove = async (sagraId) => {
    setActionLoading(prev => ({ ...prev, [sagraId]: 'approve' }));
    try {
      await axios.put(`${API}/admin/sagre/${sagraId}/approve`);
      setPendingSagre(prev => prev.filter(s => s.id !== sagraId));
      toast.success("Sagra approvata!");
    } catch (error) {
      toast.error("Errore durante l'approvazione");
    } finally {
      setActionLoading(prev => ({ ...prev, [sagraId]: null }));
    }
  };

  const handleReject = async (sagraId) => {
    setActionLoading(prev => ({ ...prev, [sagraId]: 'reject' }));
    try {
      await axios.put(`${API}/admin/sagre/${sagraId}/reject`);
      setPendingSagre(prev => prev.filter(s => s.id !== sagraId));
      toast.success("Sagra rifiutata");
    } catch (error) {
      toast.error("Errore durante il rifiuto");
    } finally {
      setActionLoading(prev => ({ ...prev, [sagraId]: null }));
    }
  };

  const handleDelete = async (sagraId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa sagra?")) return;
    
    setActionLoading(prev => ({ ...prev, [sagraId]: 'delete' }));
    try {
      await axios.delete(`${API}/admin/sagre/${sagraId}`);
      setPendingSagre(prev => prev.filter(s => s.id !== sagraId));
      toast.success("Sagra eliminata");
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setActionLoading(prev => ({ ...prev, [sagraId]: null }));
    }
  };

  // Edit sagra functions
  const fetchProvinceForEdit = async (regione) => {
    try {
      const res = await axios.get(`${API}/province/${encodeURIComponent(regione)}`);
      setProvince(res.data.province);
    } catch (error) {
      console.error("Error fetching province:", error);
    }
  };

  const openEditModal = async (sagra) => {
    setEditingSagra(sagra);
    setEditForm({
      nome: sagra.nome,
      descrizione: sagra.descrizione,
      regione: sagra.regione,
      provincia: sagra.provincia,
      comune: sagra.comune,
      indirizzo: sagra.indirizzo,
      data_inizio: sagra.data_inizio,
      data_fine: sagra.data_fine,
      orario_apertura: sagra.orario_apertura || "",
      orario_chiusura: sagra.orario_chiusura || "",
      immagine_url: sagra.immagine_url || "",
      contatti: sagra.contatti || { nome_organizzatore: "", telefono: "", email: "" }
    });
    if (sagra.regione) {
      await fetchProvinceForEdit(sagra.regione);
    }
    setLocandinaFile(null);
  };

  const closeEditModal = () => {
    setEditingSagra(null);
    setEditForm(null);
    setLocandinaFile(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleEditContattiChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      contatti: { ...prev.contatti, [field]: value }
    }));
  };

  const handleLocandinaChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato non supportato. Usa JPG o PDF.");
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File troppo grande. Dimensione massima: 10 MB");
      return;
    }

    setLocandinaFile(file);
  };

  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      // Update sagra data
      const response = await axios.put(`${API}/admin/sagre/${editingSagra.id}`, editForm);
      
      // Upload locandina if selected
      if (locandinaFile) {
        const formDataFile = new FormData();
        formDataFile.append('file', locandinaFile);
        await axios.post(`${API}/sagre/${editingSagra.id}/locandina`, formDataFile, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      // Update local state
      setPendingSagre(prev => prev.map(s => 
        s.id === editingSagra.id ? { ...s, ...response.data } : s
      ));
      
      toast.success("Sagra aggiornata con successo!");
      closeEditModal();
    } catch (error) {
      console.error("Error updating sagra:", error);
      toast.error("Errore durante l'aggiornamento");
    } finally {
      setEditLoading(false);
    }
  };

  // CSV Import functions
  const handleDownloadTemplate = () => {
    window.open(`${API}/admin/sagre/template`, '_blank');
  };

  const handleExportSagre = () => {
    window.open(`${API}/admin/sagre/export`, '_blank');
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error("Seleziona un file CSV");
      return;
    }

    setCsvFile(file);
    setImportResults(null);
  };

  const handleCsvImport = async () => {
    if (!csvFile) {
      toast.error("Seleziona un file CSV");
      return;
    }

    setCsvImporting(true);
    setImportResults(null);

    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await axios.post(
        `${API}/admin/sagre/import?auto_approve=${csvAutoApprove}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setImportResults(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`${response.data.success_count} sagre importate con successo!`);
        // Refresh pending sagre list
        fetchData();
      }
      
      if (response.data.error_count > 0) {
        toast.error(`${response.data.error_count} righe con errori`);
      }

      // Clear file input
      setCsvFile(null);
      if (csvInputRef.current) {
        csvInputRef.current.value = '';
      }
    } catch (error) {
      console.error("Error importing CSV:", error);
      toast.error(error.response?.data?.detail || "Errore durante l'importazione");
    } finally {
      setCsvImporting(false);
    }
  };

  // Upload image for news editor
  const handleNewsImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/news/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Return full URL for the image
      return `${BACKEND_URL}${response.data.url}`;
    } catch (error) {
      toast.error("Errore durante il caricamento dell'immagine");
      return null;
    }
  };

  const handleNewsSubmit = async (e) => {
    e.preventDefault();
    if (!newsForm.titolo || !newsForm.contenuto) {
      toast.error("Titolo e contenuto sono obbligatori");
      return;
    }
    
    setNewsLoading(true);
    try {
      const response = await axios.post(`${API}/news`, newsForm);
      setNews(prev => [response.data, ...prev]);
      setNewsForm({ titolo: "", contenuto: "", categoria: "", regione: "", immagine_url: "" });
      toast.success("News pubblicata!");
    } catch (error) {
      toast.error("Errore durante la pubblicazione");
    } finally {
      setNewsLoading(false);
    }
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa news?")) return;
    
    try {
      await axios.delete(`${API}/news/${newsId}`);
      setNews(prev => prev.filter(n => n.id !== newsId));
      toast.success("News eliminata");
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/D";
    try {
      const date = parseISO(dateStr);
      if (!isValid(date)) return "N/D";
      return format(date, "d MMM yyyy", { locale: it });
    } catch {
      return dateStr || "N/D";
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700">Online</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">In attesa</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rifiutata</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4" data-testid="admin-login">
        <Card className="w-full max-w-sm border-0 shadow-elevated">
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>Accesso Admin</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Inserisci password"
                  className="mt-1.5"
                  data-testid="admin-password-input"
                />
                {passwordError && (
                  <p className="text-destructive text-sm mt-1">{passwordError}</p>
                )}
              </div>
              <Button type="submit" className="w-full rounded-lg" data-testid="admin-login-btn">
                Accedi
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl text-foreground mb-2">
              Pannello Admin
            </h1>
            <p className="text-muted-foreground">
              Gestisci sagre, news e iscrizioni
            </p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="rounded-lg">
            Esci
          </Button>
        </div>

        <Tabs defaultValue="moderation" className="space-y-6">
          <TabsList className="rounded-lg flex-wrap">
            <TabsTrigger value="moderation" className="rounded-md gap-2" data-testid="admin-tab-moderation">
              <Clock className="w-4 h-4" />
              Moderazione
              {pendingSagre.length > 0 && (
                <Badge className="ml-1 bg-secondary text-secondary-foreground">{pendingSagre.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all-sagre" className="rounded-md gap-2" data-testid="admin-tab-all-sagre">
              <Database className="w-4 h-4" />
              Tutte le Sagre
              <Badge className="ml-1 bg-primary/10 text-primary">{allSagreTotal}</Badge>
            </TabsTrigger>
            <TabsTrigger value="import" className="rounded-md gap-2" data-testid="admin-tab-import">
              <FileSpreadsheet className="w-4 h-4" />
              Import CSV
            </TabsTrigger>
            <TabsTrigger value="news" className="rounded-md gap-2" data-testid="admin-tab-news">
              <Newspaper className="w-4 h-4" />
              News
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="rounded-md gap-2" data-testid="admin-tab-newsletter">
              <Mail className="w-4 h-4" />
              Newsletter
            </TabsTrigger>
          </TabsList>

          {/* Moderation Tab */}
          <TabsContent value="moderation" className="space-y-6">
            {pendingSagre.length === 0 ? (
              <Card className="border-0 shadow-subtle">
                <CardContent className="py-16 text-center">
                  <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Nessuna sagra in attesa
                  </h3>
                  <p className="text-muted-foreground">
                    Tutte le sagre sono state moderate.
                  </p>
                </CardContent>
              </Card>
            ) : (
              pendingSagre.map((sagra) => (
                <Card key={sagra.id} className="border-0 shadow-subtle overflow-hidden" data-testid={`pending-sagra-${sagra.id}`}>
                  <div className="flex flex-col lg:flex-row">
                    <div className="lg:w-64 h-48 lg:h-auto bg-muted flex-shrink-0">
                      <img
                        src={sagra.immagine_url || "https://images.unsplash.com/photo-1504674900247-0877df9cc836"}
                        alt={sagra.nome}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 p-6">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-foreground mb-2">{sagra.nome}</h3>
                          <div className="space-y-1 text-sm text-muted-foreground mb-3">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{sagra.comune}, {sagra.provincia} - {sagra.regione}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(sagra.data_inizio)} - {formatDate(sagra.data_fine)}</span>
                            </div>
                            {sagra.locandina_filename && (
                              <div className="flex items-center gap-2 text-primary">
                                <FileText className="w-4 h-4" />
                                <span>Locandina presente</span>
                              </div>
                            )}
                          </div>
                          <p className="text-muted-foreground line-clamp-2">{sagra.descrizione}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-lg text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => openEditModal(sagra)}
                            disabled={!!actionLoading[sagra.id]}
                            title="Modifica"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-lg text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => handleApprove(sagra.id)}
                            disabled={!!actionLoading[sagra.id]}
                            title="Approva"
                          >
                            {actionLoading[sagra.id] === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-lg text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleReject(sagra.id)}
                            disabled={!!actionLoading[sagra.id]}
                            title="Rifiuta"
                          >
                            {actionLoading[sagra.id] === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-lg text-muted-foreground hover:text-destructive"
                            onClick={() => handleDelete(sagra.id)}
                            disabled={!!actionLoading[sagra.id]}
                            title="Elimina"
                          >
                            {actionLoading[sagra.id] === 'delete' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* All Sagre Tab */}
          <TabsContent value="all-sagre" className="space-y-6">
            {/* Filters */}
            <Card className="border-0 shadow-subtle">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Cerca</Label>
                    <div className="relative mt-1.5">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Nome, comune..."
                        value={allSagreFilter.search}
                        onChange={(e) => handleAllSagreFilterChange('search', e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Stato</Label>
                    <Select
                      value={allSagreFilter.status || "all"}
                      onValueChange={(value) => handleAllSagreFilterChange('status', value === "all" ? "" : value)}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Tutti gli stati" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti</SelectItem>
                        <SelectItem value="approved">Online</SelectItem>
                        <SelectItem value="pending">In attesa</SelectItem>
                        <SelectItem value="rejected">Rifiutate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Regione</Label>
                    <Select
                      value={allSagreFilter.regione || "all"}
                      onValueChange={(value) => handleAllSagreFilterChange('regione', value === "all" ? "" : value)}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Tutte le regioni" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutte</SelectItem>
                        {regioni.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setAllSagreFilter({ status: '', regione: '', search: '' });
                        fetchAllSagre({ status: '', regione: '', search: '' });
                      }}
                      className="w-full rounded-lg"
                    >
                      Reset filtri
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground">
                {allSagreTotal} sagre trovate
              </p>
              <Button variant="outline" onClick={handleExportSagre} className="gap-2 rounded-lg">
                <Download className="w-4 h-4" />
                Esporta CSV
              </Button>
            </div>

            {allSagreLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : allSagre.length === 0 ? (
              <Card className="border-0 shadow-subtle">
                <CardContent className="py-16 text-center">
                  <Database className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Nessuna sagra trovata
                  </h3>
                  <p className="text-muted-foreground">
                    Prova a modificare i filtri di ricerca.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {allSagre.map((sagra) => (
                  <Card key={sagra.id} className="border-0 shadow-subtle overflow-hidden">
                    <div className="flex flex-col sm:flex-row">
                      <div className="sm:w-32 h-24 sm:h-auto bg-muted flex-shrink-0">
                        <img
                          src={sagra.immagine_url || "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=200"}
                          alt={sagra.nome}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-foreground truncate">{sagra.nome}</h3>
                              {getStatusBadge(sagra.status)}
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {sagra.comune}, {sagra.provincia}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {formatDate(sagra.data_inizio)} - {formatDate(sagra.data_fine)}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Link to={`/sagra/${sagra.id}`} target="_blank">
                              <Button variant="outline" size="icon" className="rounded-lg" title="Visualizza">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button
                              variant="outline"
                              size="icon"
                              className="rounded-lg text-blue-600 border-blue-200 hover:bg-blue-50"
                              onClick={() => openEditModal(sagra)}
                              disabled={!!actionLoading[sagra.id]}
                              title="Modifica"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {sagra.status !== 'approved' && (
                              <Button
                                variant="outline"
                                size="icon"
                                className="rounded-lg text-green-600 border-green-200 hover:bg-green-50"
                                onClick={() => handleChangeSagraStatus(sagra.id, 'approved')}
                                disabled={!!actionLoading[sagra.id]}
                                title="Pubblica"
                              >
                                {actionLoading[sagra.id] === 'status' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                              </Button>
                            )}
                            {sagra.status === 'approved' && (
                              <Button
                                variant="outline"
                                size="icon"
                                className="rounded-lg text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                                onClick={() => handleChangeSagraStatus(sagra.id, 'rejected')}
                                disabled={!!actionLoading[sagra.id]}
                                title="Nascondi"
                              >
                                {actionLoading[sagra.id] === 'status' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="icon"
                              className="rounded-lg text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleDeleteSagraFromAll(sagra.id)}
                              disabled={!!actionLoading[sagra.id]}
                              title="Elimina"
                            >
                              {actionLoading[sagra.id] === 'delete' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Import CSV Tab */}
          <TabsContent value="import" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Import Section */}
              <Card className="border-0 shadow-subtle">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Importa Sagre da CSV
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Instructions */}
                  <div className="bg-muted/50 rounded-lg p-4 text-sm">
                    <h4 className="font-medium mb-2">Istruzioni:</h4>
                    <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                      <li>Scarica il template CSV</li>
                      <li>Compila i dati delle sagre (usa Excel o Google Sheets)</li>
                      <li>Salva come CSV con separatore <code className="bg-muted px-1 rounded">;</code> (punto e virgola)</li>
                      <li>Carica il file qui sotto</li>
                    </ol>
                  </div>

                  {/* Download Template */}
                  <div>
                    <Button 
                      variant="outline" 
                      onClick={handleDownloadTemplate}
                      className="w-full rounded-lg gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Scarica Template CSV
                    </Button>
                  </div>

                  {/* File Upload */}
                  <div>
                    <Label>File CSV</Label>
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleCsvFileChange}
                      className="hidden"
                      id="csv-upload"
                    />
                    {!csvFile ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => csvInputRef.current?.click()}
                        className="w-full mt-1.5 gap-2 h-20 border-dashed"
                      >
                        <FileSpreadsheet className="w-6 h-6" />
                        <span>Seleziona file CSV</span>
                      </Button>
                    ) : (
                      <div className="mt-1.5 p-4 bg-muted/50 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileSpreadsheet className="w-8 h-8 text-green-600" />
                          <div>
                            <p className="font-medium">{csvFile.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(csvFile.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setCsvFile(null);
                            setImportResults(null);
                            if (csvInputRef.current) csvInputRef.current.value = '';
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Auto Approve Option */}
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div>
                      <Label htmlFor="auto-approve" className="font-medium">Approva automaticamente</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Le sagre saranno pubblicate subito senza moderazione
                      </p>
                    </div>
                    <Switch
                      id="auto-approve"
                      checked={csvAutoApprove}
                      onCheckedChange={setCsvAutoApprove}
                    />
                  </div>

                  {/* Import Button */}
                  <Button 
                    onClick={handleCsvImport}
                    disabled={!csvFile || csvImporting}
                    className="w-full rounded-lg gap-2"
                  >
                    {csvImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Importazione in corso...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Importa Sagre
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Results / Export Section */}
              <div className="space-y-6">
                {/* Import Results */}
                {importResults && (
                  <Card className="border-0 shadow-subtle">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        {importResults.error_count === 0 ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-amber-500" />
                        )}
                        Risultato Importazione
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-3 mb-4">
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold">{importResults.total_rows}</p>
                          <p className="text-xs text-muted-foreground">Righe</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <p className="text-2xl font-bold text-green-600">{importResults.success_count}</p>
                          <p className="text-xs text-muted-foreground">Importate</p>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-lg">
                          <p className="text-2xl font-bold text-yellow-600">{importResults.skipped_count || 0}</p>
                          <p className="text-xs text-muted-foreground">Duplicate</p>
                        </div>
                        <div className="text-center p-3 bg-red-50 rounded-lg">
                          <p className="text-2xl font-bold text-red-600">{importResults.error_count}</p>
                          <p className="text-xs text-muted-foreground">Errori</p>
                        </div>
                      </div>

                      {/* Warnings */}
                      {importResults.warnings && importResults.warnings.length > 0 && (
                        <div className="mt-4">
                          <h4 className="font-medium mb-2 text-orange-600">⚠️ Avvisi (importate con dati parziali):</h4>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {importResults.warnings.map((item, idx) => (
                              <div key={idx} className="text-sm p-2 bg-orange-50 rounded">
                                <span className="font-medium text-orange-700">Riga {item.row}:</span>{" "}
                                <span className="text-orange-600">{item.warning}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {importResults.skipped && importResults.skipped.length > 0 && (
                        <div className="mt-4">
                          <h4 className="font-medium mb-2 text-yellow-600">Saltate (già esistenti):</h4>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {importResults.skipped.map((item, idx) => (
                              <div key={idx} className="text-sm p-2 bg-yellow-50 rounded">
                                <span className="text-yellow-700">{item.nome}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {importResults.errors.length > 0 && (
                        <div className="mt-4">
                          <h4 className="font-medium mb-2 text-red-600">Errori:</h4>
                          <div className="max-h-48 overflow-y-auto space-y-2">
                            {importResults.errors.map((err, idx) => (
                              <div key={idx} className="text-sm p-2 bg-red-50 rounded flex gap-2">
                                <span className="font-medium text-red-700">Riga {err.row}:</span>
                                <span className="text-red-600">{err.error}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {importResults.imported.length > 0 && (
                        <div className="mt-4">
                          <h4 className="font-medium mb-2 text-green-600">Importate con successo:</h4>
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {importResults.imported.slice(0, 10).map((item, idx) => (
                              <div key={idx} className="text-sm p-2 bg-green-50 rounded flex justify-between">
                                <span>{item.nome}</span>
                                <Badge variant="outline" className="text-xs">
                                  {item.status === 'approved' ? 'Pubblicata' : 'In attesa'}
                                </Badge>
                              </div>
                            ))}
                            {importResults.imported.length > 10 && (
                              <p className="text-xs text-muted-foreground text-center pt-2">
                                ... e altre {importResults.imported.length - 10} sagre
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Export Section */}
                <Card className="border-0 shadow-subtle">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Download className="w-5 h-5" />
                      Esporta Sagre
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Scarica tutte le sagre in formato CSV per backup o modifica.
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={handleExportSagre}
                      className="w-full rounded-lg gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Esporta tutte le Sagre
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news" className="space-y-6">
            {/* Add News Form */}
            <Card className="border-0 shadow-subtle">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Pubblica News
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleNewsSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="titolo">Titolo *</Label>
                    <Input
                      id="titolo"
                      value={newsForm.titolo}
                      onChange={(e) => setNewsForm(prev => ({ ...prev, titolo: e.target.value }))}
                      placeholder="Titolo della news"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>Contenuto *</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Usa la barra degli strumenti per formattare il testo, aggiungere titoli, immagini e link.
                    </p>
                    <Suspense fallback={
                      <div className="border border-input rounded-lg p-4 min-h-[300px] flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                      </div>
                    }>
                      <RichTextEditor
                        content={newsForm.contenuto}
                        onChange={(html) => setNewsForm(prev => ({ ...prev, contenuto: html }))}
                        onImageUpload={handleNewsImageUpload}
                        placeholder="Scrivi il contenuto della news..."
                      />
                    </Suspense>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="categoria">Categoria</Label>
                      <Select
                        value={newsForm.categoria || "none"}
                        onValueChange={(value) => setNewsForm(prev => ({ ...prev, categoria: value === "none" ? "" : value }))}
                      >
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Novità">Novità</SelectItem>
                          <SelectItem value="Eventi">Eventi</SelectItem>
                          <SelectItem value="Tradizioni">Tradizioni</SelectItem>
                          <SelectItem value="Territorio">Territorio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="regione">Regione</Label>
                      <Select
                        value={newsForm.regione || "none"}
                        onValueChange={(value) => setNewsForm(prev => ({ ...prev, regione: value === "none" ? "" : value }))}
                      >
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuna</SelectItem>
                          {regioni.map((reg) => (
                            <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="immagine">Immagine di copertina (URL)</Label>
                      <Input
                        id="immagine"
                        value={newsForm.immagine_url}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, immagine_url: e.target.value }))}
                        placeholder="https://..."
                        className="mt-1.5"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Oppure carica un'immagine nell'editor e copia l'URL
                      </p>
                    </div>
                  </div>
                  <Button type="submit" disabled={newsLoading} className="rounded-lg">
                    {newsLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Newspaper className="w-4 h-4 mr-2" />}
                    Pubblica News
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* News List */}
            <Card className="border-0 shadow-subtle">
              <CardHeader>
                <CardTitle>News Pubblicate ({news.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {news.length > 0 ? (
                  <div className="divide-y divide-border">
                    {news.map((item) => (
                      <div key={item.id} className="py-4 flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {item.categoria && <Badge variant="secondary" className="text-xs">{item.categoria}</Badge>}
                            <span className="text-xs text-muted-foreground">{formatDate(item.data_pubblicazione)}</span>
                          </div>
                          <h4 className="font-medium text-foreground">{item.titolo}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-2">{item.contenuto}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => handleDeleteNews(item.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">Nessuna news pubblicata.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Newsletter Tab */}
          <TabsContent value="newsletter" className="space-y-6">
            <Card className="border-0 shadow-subtle">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Iscritti Newsletter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-primary/10 rounded-xl p-4">
                    <p className="text-3xl font-bold text-primary">{newsletterSubs.count || 0}</p>
                    <p className="text-sm text-muted-foreground">Iscritti attivi</p>
                  </div>
                </div>
                
                {newsletterSubs.subscribers && newsletterSubs.subscribers.length > 0 ? (
                  <div className="divide-y divide-border">
                    {newsletterSubs.subscribers.slice(0, 20).map((sub) => (
                      <div key={sub.id} className="py-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-foreground">{sub.email}</p>
                          {sub.regione && <p className="text-sm text-muted-foreground">Regione: {sub.regione}</p>}
                        </div>
                        <Badge variant="secondary" className="rounded-md">
                          {formatDate(sub.data_iscrizione)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">Nessun iscritto alla newsletter.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Sagra Dialog */}
      <Dialog open={!!editingSagra} onOpenChange={(open) => !open && closeEditModal()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Sagra</DialogTitle>
          </DialogHeader>
          
          {editForm && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="edit-nome">Nome Sagra</Label>
                  <Input
                    id="edit-nome"
                    value={editForm.nome}
                    onChange={(e) => handleEditChange("nome", e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label htmlFor="edit-descrizione">Descrizione</Label>
                  <Textarea
                    id="edit-descrizione"
                    value={editForm.descrizione}
                    onChange={(e) => handleEditChange("descrizione", e.target.value)}
                    rows={3}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label>Regione</Label>
                  <Select
                    value={editForm.regione}
                    onValueChange={(value) => {
                      handleEditChange("regione", value);
                      fetchProvinceForEdit(value);
                    }}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent>
                      {regioni.map((reg) => (
                        <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Provincia</Label>
                  <Select
                    value={editForm.provincia}
                    onValueChange={(value) => handleEditChange("provincia", value)}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent>
                      {province.map((prov) => (
                        <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="edit-comune">Comune</Label>
                  <Input
                    id="edit-comune"
                    value={editForm.comune}
                    onChange={(e) => handleEditChange("comune", e.target.value)}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-indirizzo">Indirizzo</Label>
                  <Input
                    id="edit-indirizzo"
                    value={editForm.indirizzo}
                    onChange={(e) => handleEditChange("indirizzo", e.target.value)}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-data-inizio">Data Inizio</Label>
                  <Input
                    id="edit-data-inizio"
                    type="date"
                    value={editForm.data_inizio}
                    onChange={(e) => handleEditChange("data_inizio", e.target.value)}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-data-fine">Data Fine</Label>
                  <Input
                    id="edit-data-fine"
                    type="date"
                    value={editForm.data_fine}
                    onChange={(e) => handleEditChange("data_fine", e.target.value)}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-orario-apertura">Orario Apertura</Label>
                  <Input
                    id="edit-orario-apertura"
                    type="time"
                    value={editForm.orario_apertura}
                    onChange={(e) => handleEditChange("orario_apertura", e.target.value)}
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-orario-chiusura">Orario Chiusura</Label>
                  <Input
                    id="edit-orario-chiusura"
                    type="time"
                    value={editForm.orario_chiusura}
                    onChange={(e) => handleEditChange("orario_chiusura", e.target.value)}
                    className="mt-1.5"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="edit-immagine">URL Immagine</Label>
                  <Input
                    id="edit-immagine"
                    value={editForm.immagine_url}
                    onChange={(e) => handleEditChange("immagine_url", e.target.value)}
                    placeholder="https://..."
                    className="mt-1.5"
                  />
                </div>

                {/* Locandina Upload */}
                <div className="md:col-span-2">
                  <Label>Carica Locandina</Label>
                  <p className="text-xs text-muted-foreground mb-2">JPG o PDF, max 10 MB</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.pdf"
                    onChange={handleLocandinaChange}
                    className="hidden"
                    id="edit-locandina-upload"
                  />
                  {!locandinaFile ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full gap-2 h-16 border-dashed"
                    >
                      <Upload className="w-5 h-5" />
                      <span>Seleziona locandina</span>
                    </Button>
                  ) : (
                    <div className="p-3 bg-muted/50 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {locandinaFile.type === 'application/pdf' ? (
                          <FileText className="w-5 h-5 text-red-500" />
                        ) : (
                          <Image className="w-5 h-5 text-blue-500" />
                        )}
                        <div>
                          <p className="text-sm font-medium truncate max-w-[200px]">{locandinaFile.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(locandinaFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setLocandinaFile(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  {editingSagra?.locandina_filename && !locandinaFile && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      Locandina già presente
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <Label>Contatti Organizzatore</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-1.5">
                    <Input
                      placeholder="Nome organizzatore"
                      value={editForm.contatti.nome_organizzatore}
                      onChange={(e) => handleEditContattiChange("nome_organizzatore", e.target.value)}
                    />
                    <Input
                      placeholder="Telefono"
                      value={editForm.contatti.telefono}
                      onChange={(e) => handleEditContattiChange("telefono", e.target.value)}
                    />
                    <Input
                      placeholder="Email"
                      type="email"
                      value={editForm.contatti.email}
                      onChange={(e) => handleEditContattiChange("email", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeEditModal} disabled={editLoading}>
              Annulla
            </Button>
            <Button onClick={handleSaveEdit} disabled={editLoading}>
              {editLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Salvataggio...
                </>
              ) : (
                "Salva modifiche"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
